import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as BillIcon,
  LocalShipping as ReceiveIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { PurchaseService } from '../../../services/purchaseService';
import { PurchaseOrder, PurchaseOrderStatus } from '../../../types/purchase';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import PurchaseOrderPDFPreview from '../../../components/purchase-orders/PurchaseOrderPDFPreview';

const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);

  // Dialog states
  const [convertToBillOpen, setConvertToBillOpen] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<PurchaseOrderStatus>(PurchaseOrderStatus.SENT);
  const [statusNotes, setStatusNotes] = useState('');

  // Fetch purchase order data
  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await PurchaseService.getPurchaseOrder(id);
        setPurchaseOrder(data);
      } catch (error: any) {
        console.error('Failed to fetch purchase order:', error);
        setError(error?.response?.data?.detail || 'Failed to load purchase order');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrder();
  }, [id]);

  const getStatusColor = useCallback((status: PurchaseOrderStatus) => {
    switch (status) {
      case PurchaseOrderStatus.DRAFT:
        return 'default';
      case PurchaseOrderStatus.SENT:
        return 'info';
      case PurchaseOrderStatus.ACKNOWLEDGED:
        return 'primary';
      case PurchaseOrderStatus.PARTIAL_RECEIVED:
        return 'warning';
      case PurchaseOrderStatus.RECEIVED:
        return 'success';
      case PurchaseOrderStatus.BILLED:
        return 'success';
      case PurchaseOrderStatus.CLOSED:
        return 'default';
      default:
        return 'default';
    }
  }, []);

  const permissions = useMemo(() => {
    if (!purchaseOrder) return {
      canEdit: false,
      canSend: false,
      canAcknowledge: false,
      canReceive: false,
      canConvertToBill: false,
      canCancel: false,
      canUpdateStatus: false
    };

    return {
      canEdit: purchaseOrder.status === PurchaseOrderStatus.DRAFT,
      canSend: purchaseOrder.status === PurchaseOrderStatus.DRAFT,
      canAcknowledge: purchaseOrder.status === PurchaseOrderStatus.SENT,
      canReceive: [
        PurchaseOrderStatus.ACKNOWLEDGED,
        PurchaseOrderStatus.PARTIAL_RECEIVED
      ].includes(purchaseOrder.status),
      canConvertToBill: ![PurchaseOrderStatus.CLOSED, PurchaseOrderStatus.BILLED].includes(purchaseOrder.status),
      canCancel: ![PurchaseOrderStatus.CLOSED, PurchaseOrderStatus.BILLED].includes(purchaseOrder.status),
      canUpdateStatus: ![PurchaseOrderStatus.CLOSED].includes(purchaseOrder.status)
    };
  }, [purchaseOrder?.status]);

  // Valid status transitions based on current status
  const validStatusTransitions = useMemo(() => {
    if (!purchaseOrder) return [];
    
    const transitions: Record<PurchaseOrderStatus, PurchaseOrderStatus[]> = {
      [PurchaseOrderStatus.DRAFT]: [
        PurchaseOrderStatus.SENT,
        PurchaseOrderStatus.ACKNOWLEDGED,
        PurchaseOrderStatus.PARTIAL_RECEIVED,
        PurchaseOrderStatus.RECEIVED,
        PurchaseOrderStatus.BILLED
      ],
      [PurchaseOrderStatus.SENT]: [
        PurchaseOrderStatus.ACKNOWLEDGED,
        PurchaseOrderStatus.PARTIAL_RECEIVED,
        PurchaseOrderStatus.RECEIVED
      ],
      [PurchaseOrderStatus.ACKNOWLEDGED]: [
        PurchaseOrderStatus.PARTIAL_RECEIVED,
        PurchaseOrderStatus.RECEIVED,
        PurchaseOrderStatus.BILLED
      ],
      [PurchaseOrderStatus.PARTIAL_RECEIVED]: [
        PurchaseOrderStatus.RECEIVED,
        PurchaseOrderStatus.BILLED
      ],
      [PurchaseOrderStatus.RECEIVED]: [PurchaseOrderStatus.BILLED],
      [PurchaseOrderStatus.BILLED]: [PurchaseOrderStatus.CLOSED],
      [PurchaseOrderStatus.CLOSED]: [],
    };
    
    return transitions[purchaseOrder.status] || [];
  }, [purchaseOrder?.status]);

  const handleEdit = () => {
    navigate(`/admin/purchase/orders/${id}/edit`);
  };

  const handleTogglePDFPreview = () => {
    setShowPDFPreview(!showPDFPreview);
  };

  const handleSend = async () => {
    if (!purchaseOrder) return;

    try {
      setActionLoading(true);
      await PurchaseService.sendPurchaseOrder(purchaseOrder.id);
      const updatedOrder = await PurchaseService.getPurchaseOrder(purchaseOrder.id);
      setPurchaseOrder(updatedOrder);
      enqueueSnackbar('Purchase order sent successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to send purchase order:', error);
      enqueueSnackbar('Failed to send purchase order', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!purchaseOrder) return;

    try {
      setActionLoading(true);
      await PurchaseService.acknowledgePurchaseOrder(purchaseOrder.id);
      const updatedOrder = await PurchaseService.getPurchaseOrder(purchaseOrder.id);
      setPurchaseOrder(updatedOrder);
      enqueueSnackbar('Purchase order acknowledged successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to acknowledge purchase order:', error);
      enqueueSnackbar('Failed to acknowledge purchase order', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceiveGoods = () => {
    navigate(`/admin/purchase/orders/${id}/receive`);
  };

  const handleCancel = async () => {
    if (!purchaseOrder) return;

    try {
      setActionLoading(true);
      await PurchaseService.deletePurchaseOrder(purchaseOrder.id);
      enqueueSnackbar('Purchase order cancelled successfully', { variant: 'success' });
      navigate('/admin/purchase/orders');
    } catch (error: any) {
      console.error('Failed to cancel purchase order:', error);
      enqueueSnackbar('Failed to cancel purchase order', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToBill = async () => {
    if (!purchaseOrder) return;

    try {
      setActionLoading(true);
      const result = await PurchaseService.convertToBill(purchaseOrder.id);
      
      enqueueSnackbar(`Bill created successfully`, { variant: 'success' });
      setConvertToBillOpen(false);
      
      // Navigate to bill detail page
      navigate(`/admin/purchase/bills/${result.id}`);
    } catch (error: any) {
      console.error('Failed to convert to bill:', error);
      enqueueSnackbar('Failed to convert to bill', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!purchaseOrder) return;

    try {
      setActionLoading(true);
      await PurchaseService.updatePurchaseOrderStatus(purchaseOrder.id, {
        status: newStatus,
        notes: statusNotes
      });
      const updatedOrder = await PurchaseService.getPurchaseOrder(purchaseOrder.id);
      setPurchaseOrder(updatedOrder);
      setStatusUpdateOpen(false);
      setStatusNotes('');
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' 
      ? error 
      : error?.response?.data?.detail 
      ? (Array.isArray(error.response.data.detail) 
          ? error.response.data.detail[0]?.msg || 'An error occurred'
          : error.response.data.detail)
      : error?.message || 'Failed to load purchase order';
    
    return (
      <Box>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box>
        <Alert severity="error">Purchase order not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/dashboard')}
          sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/purchase/orders')}
          sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          Purchase Orders
        </Link>
        <Typography color="text.primary">
          {purchaseOrder.poNumber}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/purchase/orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Purchase Order {purchaseOrder.poNumber}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={purchaseOrder.status.toUpperCase().replace('_', ' ')}
                color={getStatusColor(purchaseOrder.status) as any}
                size="small"
              />
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant={showPDFPreview ? "contained" : "outlined"}
            startIcon={<ViewIcon />}
            onClick={handleTogglePDFPreview}
          >
            {showPDFPreview ? 'Hide PDF' : 'Show PDF'}
          </Button>
          
          {permissions.canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
          
          {permissions.canUpdateStatus && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                if (validStatusTransitions.length > 0) {
                  setNewStatus(validStatusTransitions[0]);
                }
                setStatusUpdateOpen(true);
              }}
            >
              Update Status
            </Button>
          )}
          
          {permissions.canSend && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={actionLoading}
            >
              Send to Supplier
            </Button>
          )}
          
          {permissions.canAcknowledge && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ConfirmIcon />}
              onClick={handleAcknowledge}
              disabled={actionLoading}
            >
              Acknowledge
            </Button>
          )}
          
          {permissions.canReceive && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<ReceiveIcon />}
              onClick={handleReceiveGoods}
            >
              Receive Goods
            </Button>
          )}
          
          {permissions.canConvertToBill && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<BillIcon />}
              onClick={() => setConvertToBillOpen(true)}
              disabled={actionLoading}
            >
              Convert to Bill
            </Button>
          )}
          
          {permissions.canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={actionLoading}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Purchase Order Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    PO Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {purchaseOrder.poNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(purchaseOrder.orderDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Expected Delivery
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.expectedDelivery 
                      ? formatDate(purchaseOrder.expectedDelivery)
                      : 'Not specified'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={purchaseOrder.status.toUpperCase().replace('_', ' ')}
                      color={getStatusColor(purchaseOrder.status) as any}
                      size="small"
                    />
                  </Box>
                </Grid>
                {purchaseOrder.createdByAdminName && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created By
                    </Typography>
                    <Typography variant="body1">
                      {purchaseOrder.createdByAdminName}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Supplier Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {purchaseOrder.supplierName || 'N/A'}
                  </Typography>
                  {purchaseOrder.supplierGstin && (
                    <Typography variant="body2" color="text.secondary">
                      GSTIN: {purchaseOrder.supplierGstin}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Tax</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.productName || 'No Product'} - {item.variantName || 'No Variant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.variantSku || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">
                          {item.discountPercent > 0 ? `${item.discountPercent}%` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.taxAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item.lineTotal || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          {purchaseOrder.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {purchaseOrder.notes}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* PDF Preview */}
          {showPDFPreview && (
            <Box sx={{ mt: 2 }}>
              <PurchaseOrderPDFPreview purchaseOrder={purchaseOrder} />
            </Box>
          )}
        </Grid>

        {/* Summary and Actions */}
        <Grid item xs={12} md={4}>
          {/* Order Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal:</Typography>
                <Typography>{formatCurrency(purchaseOrder.subtotal)}</Typography>
              </Box>
              {purchaseOrder.shippingCharges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Shipping:</Typography>
                  <Typography>{formatCurrency(purchaseOrder.shippingCharges)}</Typography>
                </Box>
              )}
              {purchaseOrder.packingCharges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Packing:</Typography>
                  <Typography>{formatCurrency(purchaseOrder.packingCharges)}</Typography>
                </Box>
              )}
              {purchaseOrder.discountAmount > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="error">Discount:</Typography>
                  <Typography color="error">-{formatCurrency(purchaseOrder.discountAmount)}</Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Tax:</Typography>
                <Typography>{formatCurrency(purchaseOrder.taxAmount)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Grand Total:</Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(purchaseOrder.grandTotal)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Convert to Bill Dialog */}
      <Dialog open={convertToBillOpen} onClose={() => setConvertToBillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Purchase Bill</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create a purchase bill from this purchase order.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Current Status:</strong> {purchaseOrder.status.toUpperCase().replace('_', ' ')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You can convert a purchase order to a bill at any stage. The bill will include all items from this purchase order.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertToBillOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConvertToBill}
            variant="contained"
            color="primary"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Convert to Bill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Purchase Order Status</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Current Status:</strong> {purchaseOrder.status.toUpperCase().replace('_', ' ')}
            </Typography>
          </Alert>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as PurchaseOrderStatus)}
              label="New Status"
            >
              {validStatusTransitions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.toUpperCase().replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={statusNotes}
            onChange={(e) => setStatusNotes(e.target.value)}
            placeholder="Add any notes about this status change..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            color="primary"
            disabled={actionLoading || validStatusTransitions.length === 0}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderDetail;
